import {Component, ConfigOptions} from 'panel';
import {JsxVNode} from 'snabbdom-jsx-lite';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';

import {template} from './template';
import './index.styl';

export interface SaveRecoveryCodesPageState {
  acknowledged: boolean;
  downloadInProgress: boolean;
  copyInProgress: boolean;
  loggingIn: boolean;
  error: boolean;
}

export class SaveRecoveryCodesPage extends Component<SaveRecoveryCodesPageState> {
  private form: HTMLFormElement;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'original-recovery-codes': {
        type: `json`,
        default: [],
        description: `unhashed recovery codes for users to save in case they lose their phone`,
      },
    };
  }

  get config(): ConfigOptions<SaveRecoveryCodesPageState> {
    return {
      template,
      defaultState: {
        copyInProgress: false,
        acknowledged: false,
        downloadInProgress: false,
        loggingIn: false,
        error: false,
      },
      helpers: {
        onInsert: (node: JsxVNode): void => {
          this.form = node.elm as HTMLFormElement;
        },
        onClickCopyButton: async (): Promise<void> => {
          this.update({copyInProgress: true});
          await navigator.clipboard.writeText(this.attr(`original-recovery-codes`).join(`,`));
          addToast({message: `Recovery codes copied to the clipboard`, type: ToastType.SUCCESS});
          mixpanel.track(`[2FA] Copied Recovery Codes`);
          this.update({copyInProgress: false});
        },
        onClickDownloadButton: (): void => {
          this.update({downloadInProgress: true});
          const element = document.getElementById(`download-link`);
          element.click();
          mixpanel.track(`[2FA] Downloaded Recovery Codes`);
          this.update({downloadInProgress: false});
        },
        checkCheckbox: (): void => {
          this.update({acknowledged: true, error: false});
        },
        uncheckCheckbox: (): void => {
          this.update({acknowledged: false});
        },
        onClickLoginButton: (): void => {
          if (!this.state.acknowledged) {
            mixpanel.track(`[2FA] Attempted to Login without Checking Acknowledgement`);
            this.update({error: true});
          } else {
            // no need to update after submit as submit always redirects users to a new page.
            this.update({loggingIn: true});
            this.form.submit();
          }
        },
      },
    };
  }

  connectedCallback(): void {
    super.connectedCallback();
    mixpanel.track(`[2FA] Viewed TOTP Save Your Recovery Page`);
  }
}

export const SaveRecoveryCodesPageEl = defineAndMakeJsxFactory(`mp-save-recovery-code-page`, SaveRecoveryCodesPage);
